/**
 * Common Authentication Utilities
 * Shared authentication patterns for admin and employee pages
 */

class AuthUtils {
  /**
   * Get JWT token from localStorage
   * @returns {string|null} JWT token or null if not found
   */
  static getJwtToken() {
    return localStorage.getItem("jwtToken");
  }

  /**
   * Decode JWT payload
   * @param {string} token - JWT token
   * @returns {object|null} Decoded payload or null if invalid
   */
  static decodeJwtPayload(token) {
    if (!token) return null;
    
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (error) {
      console.error('Invalid JWT token:', error);
      return null;
    }
  }

  /**
   * Get current user info from JWT token
   * @returns {object|null} User info object or null if not authenticated
   */
  static getCurrentUser() {
    const token = this.getJwtToken();
    if (!token) return null;

    const payload = this.decodeJwtPayload(token);
    if (!payload) return null;

    return {
      id: payload.userId,
      organizationId: payload.organizationId,
      role: payload.role,
      name: payload.name || payload.username,
      email: payload.email
    };
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  static isAuthenticated() {
    const user = this.getCurrentUser();
    return user !== null && user.id;
  }

  /**
   * Check if current user has admin role
   * @returns {boolean} True if user is admin
   */
  static isAdmin() {
    const user = this.getCurrentUser();
    return user && user.role === 'admin';
  }

  /**
   * Check if current user has employee role
   * @returns {boolean} True if user is employee
   */
  static isEmployee() {
    const user = this.getCurrentUser();
    return user && user.role === 'employee';
  }

  /**
   * Clear authentication data and redirect to login
   * @param {string} redirectUrl - Optional redirect URL (defaults to login page)
   */
  static logout(redirectUrl = '/auth/login.html') {
    localStorage.removeItem('jwtToken');
    window.location.href = redirectUrl;
  }

  /**
   * Admin access check with automatic redirect
   * Call this in admin page init() methods
   * @returns {boolean} True if user has admin access, false if redirected
   */
  static checkAdminAccess() {
    const token = this.getJwtToken();
    if (!token) {
      this.logout('/auth/login.html');
      return false;
    }

    const payload = this.decodeJwtPayload(token);
    if (!payload) {
      this.logout('/auth/login.html');
      return false;
    }

    if (payload.role !== 'admin') {
      // Redirect non-admin users to home page
      window.location.href = '/index.html';
      return false;
    }

    return true;
  }

  /**
   * Employee access check with automatic redirect
   * Call this in employee page init() methods
   * @returns {object|null} User info if authenticated employee, null if redirected
   */
  static checkEmployeeAccess() {
    const token = this.getJwtToken();
    if (!token) {
      this.logout('/auth/login.html');
      return null;
    }

    const payload = this.decodeJwtPayload(token);
    if (!payload) {
      this.logout('/auth/login.html');
      return null;
    }

    if (!payload.userId) {
      console.error('No user ID found in token');
      this.logout('/auth/login.html');
      return null;
    }

    // Return user info for employee pages to use
    return {
      id: payload.userId,
      organizationId: payload.organizationId,
      role: payload.role,
      name: payload.name || payload.username,
      email: payload.email
    };
  }

  /**
   * Generic authentication check that works for any role
   * @param {string|string[]} requiredRoles - Required role(s) for access
   * @param {string} redirectUrl - Where to redirect if access denied
   * @returns {object|null} User info if authorized, null if redirected
   */
  static checkRoleAccess(requiredRoles, redirectUrl = '/auth/login.html') {
    const user = this.getCurrentUser();
    if (!user) {
      this.logout(redirectUrl);
      return null;
    }

    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    if (!roles.includes(user.role)) {
      window.location.href = redirectUrl;
      return null;
    }

    return user;
  }

  /**
   * Handle authentication errors in API calls
   * @param {Response} response - Fetch response object
   * @param {string} redirectUrl - Optional redirect URL
   * @returns {boolean} True if auth error was handled
   */
  static handleAuthError(response, redirectUrl = '/auth/login.html') {
    if (response.status === 401 || response.status === 403) {
      this.logout(redirectUrl);
      return true;
    }
    return false;
  }
}

// Export for use in other modules
export default AuthUtils;

// Also make available globally for legacy code compatibility
window.AuthUtils = AuthUtils;
